import { useEffect, useReducer, useRef, type ChangeEvent } from 'react';
import { Editor, EditorContent } from '@tiptap/react';
import { cn } from '@/lib/utils';

interface RichTextEditorProps {
  editor: Editor | null;
  className?: string;
  onImageSelected: (file: File) => Promise<void>;
  isUploadingImage?: boolean;
}

interface ToolbarButtonProps {
  label: string;
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
}

function ToolbarButton({ label, onClick, isActive, disabled }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'h-8 min-w-8 rounded-md border px-2 text-xs font-medium transition-colors',
        'border-border bg-background hover:bg-muted',
        isActive && 'bg-muted text-primary border-primary/40',
        disabled && 'opacity-50 cursor-not-allowed',
      )}
    >
      {label}
    </button>
  );
}

export function RichTextEditor({
  editor,
  className,
  onImageSelected,
  isUploadingImage = false,
}: RichTextEditorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [, forceRender] = useReducer((value: number) => value + 1, 0);

  useEffect(() => {
    if (!editor) return;

    const handleTransaction = () => {
      forceRender();
    };

    editor.on('transaction', handleTransaction);

    return () => {
      editor.off('transaction', handleTransaction);
    };
  }, [editor]);

  useEffect(() => {
    if (!editor) return;

    const dom = editor.view.dom;

    const handlePaste = (event: ClipboardEvent) => {
      const files = Array.from(event.clipboardData?.files ?? []).filter((file) =>
        file.type.startsWith('image/'),
      );
      if (files.length === 0) return;

      event.preventDefault();
      void onImageSelected(files[0]);
    };

    const handleDrop = (event: DragEvent) => {
      const files = Array.from(event.dataTransfer?.files ?? []).filter((file) =>
        file.type.startsWith('image/'),
      );
      if (files.length === 0) return;

      event.preventDefault();
      void onImageSelected(files[0]);
    };

    dom.addEventListener('paste', handlePaste);
    dom.addEventListener('drop', handleDrop);

    return () => {
      dom.removeEventListener('paste', handlePaste);
      dom.removeEventListener('drop', handleDrop);
    };
  }, [editor, onImageSelected]);

  const handleImageButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      await onImageSelected(file);
    } finally {
      event.currentTarget.value = '';
    }
  };

  const handleLinkClick = () => {
    if (!editor) return;

    const previousUrl = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Enter URL:', previousUrl || 'https://');

    if (url === null) {
      return; // User cancelled
    }

    if (url === '') {
      // Remove link
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    // Set or update link
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  if (!editor) {
    return (
      <div className={cn('rounded-lg border border-border min-h-[460px] bg-background', className)} />
    );
  }

  return (
    <div className={cn('rounded-lg border border-border flex flex-col bg-background overflow-hidden', className)}>
      <div className="flex flex-wrap items-center gap-2 border-b border-border p-2 bg-muted/20 shrink-0">
        <ToolbarButton
          label="B"
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          disabled={!editor.can().chain().focus().toggleBold().run()}
        />
        <ToolbarButton
          label="I"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
        />
        <ToolbarButton
          label="S"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          isActive={editor.isActive('strike')}
          disabled={!editor.can().chain().focus().toggleStrike().run()}
        />
        <ToolbarButton
          label="H"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
        />
        <ToolbarButton
          label="UL"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
        />
        <ToolbarButton
          label="OL"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
        />
        <ToolbarButton
          label="Quote"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          isActive={editor.isActive('blockquote')}
        />
        <ToolbarButton
          label="Code"
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          isActive={editor.isActive('codeBlock')}
        />
        <ToolbarButton
          label="Link"
          onClick={handleLinkClick}
          isActive={editor.isActive('link')}
        />
        <ToolbarButton
          label="X²"
          onClick={() => editor.chain().focus().toggleSuperscript().run()}
          isActive={editor.isActive('superscript')}
          disabled={!editor.can().chain().focus().toggleSuperscript().run()}
        />
        <ToolbarButton
          label="Ref"
          onClick={() => (editor.chain().focus() as any).addReferenceItem().run()}
          isActive={editor.isActive('referenceList')}
        />
        <ToolbarButton
          label={isUploadingImage ? 'Uploading...' : 'Image'}
          onClick={handleImageButtonClick}
          disabled={isUploadingImage}
        />
        <ToolbarButton
          label="Undo"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
        />
        <ToolbarButton
          label="Redo"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
        />
      </div>

      <EditorContent
        editor={editor}
        className={cn(
          'overflow-y-auto flex-1',
          '[&_.tiptap]:min-h-full [&_.tiptap]:px-4 [&_.tiptap]:py-3 [&_.tiptap]:leading-[1.7] [&_.tiptap]:outline-none',
          '[&_p]:mb-3',
          '[&_h2]:my-3 [&_h2]:text-xl [&_h2]:font-bold',
          '[&_h3]:my-3 [&_h3]:text-lg [&_h3]:font-semibold',
          '[&_ul]:my-2 [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5',
          '[&_ol]:my-2 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5',
          '[&_blockquote]:my-3 [&_blockquote]:border-l-3 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:text-text-secondary',
          '[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[0.85em]',
          '[&_pre]:my-3 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-muted [&_pre]:p-3',
          '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
          '[&_ol[data-type=referenceList]]:my-2 [&_ol[data-type=referenceList]]:mb-3 [&_ol[data-type=referenceList]]:list-none [&_ol[data-type=referenceList]]:pl-0',
          '[&_ol[data-type=referenceList]_li]:text-[13px] [&_ol[data-type=referenceList]_li]:leading-[1.6] [&_ol[data-type=referenceList]_li]:text-text-secondary [&_ol[data-type=referenceList]_li]:mb-2 [&_ol[data-type=referenceList]_li]:pl-3',
        )}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleImageSelect}
      />
    </div>
  );
}
